import React from 'react';
import {
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import { LegalPageLayout, LegalSection } from 'components/LegalPageLayout';

// ==============================|| PRIVACY POLICY ||============================== //
// Source: KiT Contact Privacy Policy, Sterling Systems Private Limited,
// Version 1.0, effective/last updated 19 July 2026.
// NOTE: two references in the source text are still open placeholders —
// the sub-processor/vendor list URL (Section 8) and the in-app account
// deletion path (Section 11). Both are rendered below as plain bracketed
// text rather than real links until those destinations exist — replace
// them before treating this as final/publish-ready.

const DATA_CATEGORIES = [
  {
    category: 'Account and identity data',
    examples:
      'name, mobile number, email address, organisation or business name, login credentials or authentication identifiers.'
  },
  {
    category: 'Contact data imported or entered by the user',
    examples:
      'names, telephone numbers, email addresses, addresses, organisation details, labels, notes and other contact fields selected by the user.'
  },
  {
    category: 'Relationship and activity data',
    examples:
      'notes, tags, reminders, follow-up dates, interaction history and other information voluntarily entered by the user.'
  },
  {
    category: 'Device and technical data',
    examples: 'device model, operating system, app version, IP address, language, diagnostic logs, crash information and security events.'
  },
  {
    category: 'Subscription and transaction data',
    examples:
      'plan, subscription status, invoices, transaction reference and payment status. Complete card or banking credentials are normally processed by the payment service provider and are not intended to be stored by us.'
  },
  {
    category: 'Support communications',
    examples: 'messages, attachments and information submitted when contacting support.'
  }
];

const HOW_WE_COLLECT = [
  'Directly from you when you register, subscribe, enter, import, upload, scan or manage information.',
  'Automatically from your device and use of the service, including security, diagnostics and log information.',
  'From an organisation or account administrator that gives you access to a business account.',
  'From authorised service providers such as authentication, cloud-hosting, payment, analytics, messaging or customer-support providers.',
  'From device permissions only after the operating system or application presents the relevant permission request.'
];

const PURPOSES_OF_PROCESSING = [
  'Create and manage accounts, authenticate users and provide requested features.',
  'Store, organise, synchronise, back up, display, search, export or otherwise process user-submitted information.',
  'Provide reminders, notifications, support and service communications.',
  'Process subscriptions, invoices, refunds and payment confirmations.',
  'Protect accounts, detect abuse, investigate security incidents and maintain audit records.',
  'Improve reliability, accessibility, performance and user experience using aggregated or appropriately de-identified information where feasible.',
  'Comply with law, enforce agreements, establish or defend legal claims and respond to lawful requests.'
];

const SHARING_RECIPIENTS = [
  'cloud hosting and backup providers',
  'authentication, messaging, notification, analytics, crash-reporting and customer-support vendors',
  'payment gateways and billing providers',
  'professional advisers, auditors and insurers',
  'competent governmental, regulatory, law-enforcement or judicial authorities when legally required',
  'a successor entity in a merger, restructuring or transfer, subject to appropriate confidentiality and legal safeguards'
];

const USER_RIGHTS = [
  'Request a summary of personal data being processed and information about processing, where applicable.',
  'Request correction, completion or updating of inaccurate or incomplete personal data.',
  'Request erasure of personal data that is no longer necessary, subject to lawful retention.',
  'Withdraw consent with comparable ease to the manner in which it was given.',
  'Nominate another individual to exercise applicable rights in the event of death or incapacity.',
  'Raise a grievance with us and, after using our grievance mechanism, approach the Data Protection Board of India where legally available.'
];

const LEGAL_REFERENCES = [
  'Digital Personal Data Protection Act, 2023 (Act No. 22 of 2023), Ministry of Electronics and Information Technology.',
  'Digital Personal Data Protection Rules, 2025 and notified commencement schedule, Ministry of Electronics and Information Technology.',
  'Information Technology Act, 2000, as amended, India Code.'
];

const BulletList = ({ items }) => (
  <List sx={{ listStyleType: 'disc', pl: 3, py: 0 }}>
    {items.map((item, index) => (
      <ListItem key={index} sx={{ display: 'list-item', pl: 0, py: 0.5 }}>
        <ListItemText primary={item} />
      </ListItem>
    ))}
  </List>
);

const PrivacyPolicy = () => (
  <LegalPageLayout title="Privacy Policy" lastUpdated="19 July 2026">
    <LegalSection title="1. Purpose and Scope">
      This Privacy Policy explains how Sterling Systems Private Limited (&quot;Sterling Systems&quot;, &quot;we&quot;,
      &quot;us&quot; or &quot;our&quot;) collects, uses, stores, shares, protects and deletes personal data in
      connection with KiT Contact, a contact and business relationship management platform that helps users organise
      contacts, business connections, notes, categories, reminders, interactions and related relationship information
      across supported devices and web interfaces. It applies to the mobile application, web application, website,
      support channels and related services that link to this Policy.
      <br />
      <br />
      For the purposes of applicable Indian data protection law, Sterling Systems ordinarily acts as the Data
      Fiduciary for account, billing, support, security and direct-user data. Where a business customer determines
      why and how personal data of its customers, contacts, callers, employees or other individuals is entered into
      the service, Sterling Systems may act as a Data Processor on behalf of that business customer, subject to the
      applicable contract.
    </LegalSection>

    <LegalSection title="2. Applicable Legal Framework">
      This Policy is intended to be read consistently with the Digital Personal Data Protection Act, 2023, the
      Digital Personal Data Protection Rules, 2025 as and when their respective provisions become applicable under
      the notified commencement schedule, the Information Technology Act, 2000 and other applicable Indian laws. If a
      mandatory legal requirement conflicts with this Policy, the mandatory requirement will prevail.
    </LegalSection>

    <LegalSection title="3. Personal Data We May Collect">
      <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '32%' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Examples / description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DATA_CATEGORIES.map((row) => (
              <TableRow key={row.category}>
                <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{row.category}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{row.examples}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </LegalSection>

    <LegalSection title="4. How We Collect Data">
      <BulletList items={HOW_WE_COLLECT} />
    </LegalSection>

    <LegalSection title="5. Purposes of Processing">
      <BulletList items={PURPOSES_OF_PROCESSING} />
    </LegalSection>

    <LegalSection title="6. Consent and Other Permitted Processing">
      Where consent is required, we will seek consent through a clear notice and affirmative action. Consent may be
      withdrawn using account settings, permission settings or by contacting us. Withdrawal does not affect
      processing lawfully carried out before withdrawal and may prevent the affected feature from functioning. We may
      also process data without consent where the law permits a specified legitimate use or requires processing for
      compliance with law, court orders, emergencies, employment-related purposes or other recognised grounds.
    </LegalSection>

    <LegalSection title="7. Device Permissions">
      Depending on the platform and features selected, KiT Contact may request access to contacts, camera or
      storage/media for importing, scanning, attaching or exporting information, and notifications for reminders.
      Permission access is used only for the purpose displayed at the time of request.
    </LegalSection>

    <LegalSection title="8. Sharing and Service Providers">
      We do not sell personal data. We may disclose only the data reasonably necessary to:
      <BulletList items={SHARING_RECIPIENTS} />
      The current production version should publish or link to an up-to-date list of material third-party processors
      and their purposes at [SUB-PROCESSOR / VENDOR LIST URL].
    </LegalSection>

    <LegalSection title="9. Cross-Border Processing">
      Personal data may be processed or stored in India or in another jurisdiction used by an approved service
      provider, subject to applicable restrictions and safeguards. We will not transfer personal data to a country or
      territory prohibited by the Central Government and will comply with any applicable conditions on making data
      available outside India.
    </LegalSection>

    <LegalSection title="10. Security Safeguards">
      We use reasonable technical and organisational safeguards appropriate to the nature and risk of the data. These
      may include access controls, authentication, encryption in transit, encryption or equivalent protection at rest
      where appropriate, secure development practices, logging, vulnerability management, backups, vendor controls
      and incident-response procedures. No electronic system is completely secure, and users must protect passwords,
      devices and account access.
    </LegalSection>

    <LegalSection title="11. Data Retention and Deletion">
      We retain personal data only for as long as necessary for the stated purpose, account operation, security,
      legal compliance, dispute resolution or enforcement. Detailed periods are set out in the separate Data
      Retention Policy. Users may request account deletion through [IN-APP PATH / ACCOUNT DELETION URL] or by
      contacting the privacy email at{' '}
      <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>. Deletion may be subject to legal retention
      requirements, unresolved transactions, fraud prevention, backup cycles and establishment or defence of legal
      claims.
    </LegalSection>

    <LegalSection title="12. Your Rights and Grievance Redressal">
      <BulletList items={USER_RIGHTS} />
      Requests should be sent to <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>. We may verify
      identity and authority before acting. We will acknowledge and resolve grievances within the period required by
      applicable law and the service-level period published in the relevant notice.
    </LegalSection>

    <LegalSection title="13. Children and Persons with Disabilities">
      The service is not intentionally directed to children below 18 years unless a specific child-safe mode,
      age-verification process and verifiable consent mechanism is implemented. A parent or lawful guardian must
      provide verifiable consent where required. We do not knowingly undertake tracking, behavioural monitoring or
      targeted advertising directed at children. A lawful guardian must provide consent on behalf of a person with
      disability where the applicable law requires guardian consent.
    </LegalSection>

    <LegalSection title="14. User Responsibilities">
      Users must have a lawful basis and necessary permission to upload or process personal data relating to other
      individuals. KiT Contact is not intended to be used for unsolicited marketing, harassment, unlawful profiling,
      sale of personal data, or any activity prohibited by law.
    </LegalSection>

    <LegalSection title="15. Personal Data Breach">
      If a personal data breach occurs, we will take reasonable containment and remediation measures and provide
      notices to affected individuals and the Data Protection Board of India in the form, manner and timeframe
      required by applicable law. Users should promptly report suspected incidents to the privacy email.
    </LegalSection>

    <LegalSection title="16. Changes to this Policy">
      We may update this Policy to reflect legal, technical or service changes. Material changes will be communicated
      through the service, email, website or another reasonable channel. The effective date at the beginning
      identifies the current version. Continued use after an effective update constitutes acceptance only to the
      extent permitted by law; fresh consent will be sought where legally required.
    </LegalSection>

    <LegalSection title="17. Contact and Jurisdiction">
      Sterling Systems Private Limited, Pratima, First Floor, Market Yard Road, Adinanth Society, Maharshi Nagar, Pune,
      Maharashtra&ndash; 411037, INDIA.
      <br />
      Email: <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>
      <br />
      <br />
      This Policy is governed by the laws of India. Subject to any mandatory statutory forum or remedy, courts of
      competent jurisdiction in Pune, Maharashtra shall have exclusive jurisdiction over disputes arising from this
      Policy or the service.
    </LegalSection>

    <LegalSection title="18. Official Legal References">
      <BulletList items={LEGAL_REFERENCES} />
    </LegalSection>
  </LegalPageLayout>
);

export default PrivacyPolicy;
